import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { useSEO, getOrganizationSchema, getBreadcrumbSchema } from '../../utils/seo';

export default function Professionals() {
  // SEO Configuration
  useSEO({
    title: 'Healthcare Professional Resources - The Villarreal Health Network',
    description: 'Professional resources for healthcare providers, discharge planners, and case managers. Streamlined referral process and direct access to home healthcare network in Las Vegas.',
    keywords: 'healthcare professional resources, discharge planning, case management, professional referrals, hospital discharge Las Vegas',
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "name": "Healthcare Professional Resources",
          "description": "Resources and referral tools for healthcare professionals",
          "provider": getOrganizationSchema()
        },
        getBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'For Professionals', url: '/professionals' }
        ])
      ]
    }
  });

  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });

  const resources = [
    {
      title: 'Referral Forms & Templates',
      description: 'Downloadable physician order templates and referral forms',
      icon: 'ri-file-text-line',
      items: [
        'Home Health Physician Orders',
        'Psychiatric Evaluation Request',
        'Wound Care Assessment Form',
        'Physical Therapy Referral'
      ]
    },
    {
      title: 'Agency Contact Directory',
      description: 'Direct phone and fax numbers for urgent discharge needs',
      icon: 'ri-contacts-line',
      items: [
        'Med-Care Providers: (702) 555-1001',
        'Celebrity Home Health: (702) 555-1002',
        'Accent Senior Care: (702) 555-1003',
        'A Peace of Mind: (702) 555-1004'
      ]
    },
    {
      title: 'Insurance & Coverage',
      description: 'Coverage information and authorization requirements',
      icon: 'ri-shield-check-line',
      items: [
        'Medicare Coverage Guidelines',
        'Medicaid Waiver Programs',
        'Prior Authorization Forms',
        'Insurance Verification Process'
      ]
    },
    {
      title: 'Clinical Resources',
      description: 'Evidence-based care protocols and guidelines',
      icon: 'ri-stethoscope-line',
      items: [
        'Wound Care Protocols',
        'Medication Management Guidelines',
        'Fall Prevention Strategies',
        'Discharge Planning Checklist'
      ]
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setLoginData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 via-teal-600 to-blue-700 text-white py-24 pt-36 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/3708226486a039fb1433385c2f8c0346.jpg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <div className="mb-8">
              <span className="inline-block bg-white/20 text-white px-8 py-4 rounded-full text-xl font-semibold backdrop-blur-sm shadow-lg">
                Professional Healthcare Resources
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-8">
              Resources for Healthcare Professionals
            </h1>
            <p className="text-2xl text-blue-100 max-w-3xl mx-auto">
              Streamlined referral process, clinical resources, and direct access to our network of home healthcare providers.
            </p>
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div className="bg-white rounded-3xl shadow-2xl p-12 text-center hover:shadow-3xl transition-shadow duration-300 border border-gray-100">
              <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-blue-200 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-lg">
                <i className="ri-file-add-line text-blue-600 text-4xl"></i>
              </div>
              <h3 className="text-3xl font-semibold text-gray-900 mb-6">Quick Referral</h3>
              <p className="text-gray-600 mb-8 text-xl">Submit referrals directly with our streamlined professional form</p>
              <Link to="/referral">
                <Button className="w-full whitespace-nowrap text-lg px-8 py-4 shadow-lg">
                  <i className="ri-send-plane-line mr-2"></i>
                  Submit Referral
                </Button>
              </Link>
            </div>

            <div className="bg-white rounded-3xl shadow-2xl p-12 text-center hover:shadow-3xl transition-shadow duration-300 border border-gray-100">
              <div className="w-24 h-24 bg-gradient-to-br from-teal-100 to-teal-200 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-lg">
                <i className="ri-phone-line text-teal-600 text-4xl"></i>
              </div>
              <h3 className="text-3xl font-semibold text-gray-900 mb-6">Urgent Discharge</h3>
              <p className="text-gray-600 mb-8 text-xl">Direct line for immediate discharge planning needs</p>
              <a href="tel:+17025551234">
                <Button variant="outline" className="w-full whitespace-nowrap text-lg px-8 py-4 shadow-lg">
                  <i className="ri-phone-line mr-2"></i>
                  Call (702) 555-1234
                </Button>
              </a>
            </div>

            <div className="bg-white rounded-3xl shadow-2xl p-12 text-center hover:shadow-3xl transition-shadow duration-300 border border-gray-100">
              <div className="w-24 h-24 bg-gradient-to-br from-green-100 to-green-200 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-lg">
                <i className="ri-customer-service-line text-green-600 text-4xl"></i>
              </div>
              <h3 className="text-3xl font-semibold text-gray-900 mb-6">Care Coordination</h3>
              <p className="text-gray-600 mb-8 text-xl">Speak with our intake coordinators for complex cases</p>
              <Button 
                variant="outline" 
                className="w-full whitespace-nowrap cursor-pointer text-lg px-8 py-4 shadow-lg"
                onClick={() => document.querySelector('#vapi-widget-floating-button')?.click()}
              >
                <i className="ri-chat-3-line mr-2"></i>
                Start Chat
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Professional Login */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-8">Professional Portal Access</h2>
              <p className="text-gray-600 mb-8 text-lg">
                Access exclusive resources, track referral status, and manage your professional profile with our secure portal.
              </p>
              
              <div className="bg-gray-50 rounded-lg p-8">
                <h3 className="font-semibold text-gray-900 mb-6 text-xl">Portal Benefits:</h3>
                <ul className="space-y-4">
                  <li className="flex items-center text-gray-700 text-lg">
                    <i className="ri-check-line text-green-600 mr-3 text-xl"></i>
                    Track referral status in real-time
                  </li>
                  <li className="flex items-center text-gray-700 text-lg">
                    <i className="ri-check-line text-green-600 mr-3 text-xl"></i>
                    Access downloadable forms and resources
                  </li>
                  <li className="flex items-center text-gray-700 text-lg">
                    <i className="ri-check-line text-green-600 mr-3 text-xl"></i>
                    Pre-filled referral forms for repeat patients
                  </li>
                  <li className="flex items-center text-gray-700 text-lg">
                    <i className="ri-check-line text-green-600 mr-3 text-xl"></i>
                    Direct messaging with care coordinators
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-10">
              <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">Professional Login</h3>
              
              <form className="space-y-6">
                <div>
                  <label className="block font-medium text-gray-700 mb-3 text-lg">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={loginData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                    placeholder="your.email@hospital.com"
                  />
                </div>

                <div>
                  <label className="block font-medium text-gray-700 mb-3 text-lg">
                    Password
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={loginData.password}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                    placeholder="Enter your password"
                  />
                </div>

                <Button type="submit" className="w-full whitespace-nowrap">
                  <i className="ri-login-box-line mr-2"></i>
                  Sign In
                </Button>
              </form>

              <div className="mt-8 text-center">
                <p className="text-gray-600">
                  Don't have an account?{' '}
                  <a href="mailto:admin@careconnectnevada.com" className="text-blue-600 hover:text-blue-500">
                    Request Access
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Resources Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Clinical Resources</h2>
            <p className="text-xl text-gray-600">
              Essential tools and information for healthcare professionals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {resources.map((resource, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="flex items-start">
                  <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mr-6 flex-shrink-0">
                    <i className={`${resource.icon} text-blue-600 text-2xl`}></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">{resource.title}</h3>
                    <p className="text-gray-600 mb-6 text-lg">{resource.description}</p>
                    <ul className="space-y-3">
                      {resource.items.map((item, itemIndex) => (
                        <li key={itemIndex} className="text-gray-700 flex items-start text-lg">
                          <i className="ri-arrow-right-s-line text-blue-600 mr-2 mt-1 flex-shrink-0"></i>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Agency Network */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Network Agencies</h2>
            <p className="text-xl text-gray-600">
              Direct contact information for urgent referrals and discharge planning
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div className="bg-blue-50 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-blue-900 mb-6">
                <i className="ri-stethoscope-line mr-3"></i>
                Med-Care Providers
              </h3>
              <p className="text-blue-800 mb-6 text-lg">Primary care, psychiatric services, pain management, and wound care</p>
              <div className="space-y-3">
                <p className="text-blue-700 text-lg"><strong>Phone:</strong> (702) 555-1001</p>
                <p className="text-blue-700 text-lg"><strong>Fax:</strong> (702) 555-1011</p>
                <p className="text-blue-700 text-lg"><strong>Specializes in:</strong> Medical services, telehealth, multilingual staff</p>
              </div>
            </div>

            <div className="bg-teal-50 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-teal-900 mb-6">
                <i className="ri-nurse-line mr-3"></i>
                Celebrity Home Health
              </h3>
              <p className="text-teal-800 mb-6 text-lg">Medicare-certified skilled nursing and rehabilitation services</p>
              <div className="space-y-3">
                <p className="text-teal-700 text-lg"><strong>Phone:</strong> (702) 555-1002</p>
                <p className="text-teal-700 text-lg"><strong>Fax:</strong> (702) 555-1012</p>
                <p className="text-teal-700 text-lg"><strong>Specializes in:</strong> Skilled nursing, PT/OT/ST, wound care</p>
              </div>
            </div>

            <div className="bg-green-50 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-green-900 mb-6">
                <i className="ri-heart-line mr-3"></i>
                Accent Senior Care
              </h3>
              <p className="text-green-800 mb-6 text-lg">Non-skilled personal care and companion services</p>
              <div className="space-y-3">
                <p className="text-green-700 text-lg"><strong>Phone:</strong> (702) 555-1003</p>
                <p className="text-green-700 text-lg"><strong>Fax:</strong> (702) 555-1013</p>
                <p className="text-green-700 text-lg"><strong>Specializes in:</strong> Personal care, companionship, Medicaid waiver</p>
              </div>
            </div>

            <div className="bg-purple-50 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-purple-900 mb-6">
                <i className="ri-user-heart-line mr-3"></i>
                A Peace of Mind Caregivers
              </h3>
              <p className="text-purple-800 mb-6 text-lg">Specialized dementia and Alzheimer's care services</p>
              <div className="space-y-3">
                <p className="text-purple-700 text-lg"><strong>Phone:</strong> (702) 555-1004</p>
                <p className="text-purple-700 text-lg"><strong>Fax:</strong> (702) 555-1014</p>
                <p className="text-purple-700 text-lg"><strong>Specializes in:</strong> Dementia care, respite services, VA approved</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Need Immediate Assistance?
          </h2>
          <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto">
            Our intake coordinators are available to help with urgent discharge planning and complex care coordination.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <a href="tel:+17025551234">
              <Button variant="secondary" size="lg" className="w-full sm:w-auto whitespace-nowrap">
                <i className="ri-phone-line mr-2"></i>
                Call (702) 555-1234
              </Button>
            </a>
            <Link to="/referral">
              <Button variant="outline" size="lg" className="w-full sm:w-auto bg-white/10 border-white text-white hover:bg-white hover:text-blue-600 whitespace-nowrap">
                <i className="ri-file-text-line mr-2"></i>
                Submit Referral
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
